import {
  useDragAndDrop,
  type DraggableCollectionStartEvent,
  type Selection,
} from "react-aria-components";
import { categorySchema, getUniqueId } from "@medoma/models/activities";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Text } from "@medoma/components/Text/Text";
import { useDragAndDropContext } from "./DragAndDropContext";
import styles from "../SideBar/PlanningSideBar.module.scss";
import { useSelection } from "../Selection/SelectionContext";
import { useSelectedDateHasPassed } from "@/Utils/useSelectedDate";
import { getItemLabel } from "./dragAndDropUtils";
import type { IUnplannedOccurrenceOrGroup } from "@medoma/models/activities";

type UseDragAndDropFromSidebarParams = {
  unplannedOccurrencesAndGroups: IUnplannedOccurrenceOrGroup[] | undefined;
};

export const useDragAndDropFromSidebar = ({
  unplannedOccurrencesAndGroups,
}: UseDragAndDropFromSidebarParams) => {
  const { startDrag, endDrag } = useDragAndDropContext();
  const { clearSelection } = useSelection();

  const getOccurrenceOrGroupByFirstKey = (keys: Selection) => {
    if (!keys) {
      return undefined;
    }
    if (keys === "all") {
      return undefined;
    }

    const firstKey = keys.values().next().value;

    return unplannedOccurrencesAndGroups?.find(
      (item) => getUniqueId(item) === firstKey,
    );
  };

  const handleDragStart = (e: DraggableCollectionStartEvent) => {
    const draggedKeys = e.keys;

    const firstItem = getOccurrenceOrGroupByFirstKey(draggedKeys);

    if (firstItem) {
      startDrag({ source: "sidebar", item: firstItem });
    }
    clearSelection();
  };

  const handleDragEnd = () => {
    endDrag();
  };

  // Items from past dates are not allowed to be dragged from the sidebar
  const isDragAndDropNotAllowedFromSidebar = useSelectedDateHasPassed();

  return useDragAndDrop({
    isDisabled: isDragAndDropNotAllowedFromSidebar,
    getItems: (keys) => {
      return [...keys].map((key) => {
        const item = unplannedOccurrencesAndGroups?.find(
          (occurrenceOrGroup) => getUniqueId(occurrenceOrGroup) === key,
        );

        if (!item) {
          return {
            "medoma-item": "",
            "text-label": "",
            category: "",
          };
        }

        return {
          "medoma-item": getUniqueId(item),
          "text-label": getItemLabel(item),
          category: item.category,
        };
      });
    },
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    renderDragPreview(items) {
      const itemBeingDragged = items?.[0];
      if (!itemBeingDragged) {
        return <></>;
      }
      const draggedName = itemBeingDragged["text-label"];
      const parsedCategory = categorySchema.safeParse(
        itemBeingDragged.category,
      );
      const draggedCategory = parsedCategory.success
        ? parsedCategory.data
        : undefined;

      return (
        <div className={styles.dragPreview}>
          {draggedCategory ? (
            <CategoryIcon category={draggedCategory} size="small" />
          ) : null}
          <Text element="p">{draggedName}</Text>
        </div>
      );
    },
  });
};
